import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { P, rows, type ID, type WorkflowDefinition, type WorkflowStepDef } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useBreakpoint } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { SideMenu } from '../components/misc';
import { Alert, Badge, Button, Card, EmptyState, Grid, Icon, IconButton, Loading, PageHeader, Row, Screen, Select, Stack, Switch, Text, TextField, useTheme } from '../../../ui';

const KEYS = ['leave_request', 'regularisation', 'profile_change', 'comp_off'];
type ApproverKind = 'reporting_manager' | 'manager_of_manager' | 'role' | 'user';
type Op = '==' | '!=' | '>' | '>=' | '<' | '<=' | 'in' | 'truthy';
type When = { field: string; op: Op; value?: unknown };
/** Step as stored by the backend engine (the shared type predates `when`). */
type StepDef = WorkflowStepDef & { when?: When | null };
type Def = Omit<Partial<WorkflowDefinition>, 'steps'> & { steps?: StepDef[] };

/** Context fields each workflow exposes to step conditions (WorkflowEngine::workflowContext). */
const CONTEXT_FIELDS: Record<string, { value: string; label: string }[]> = {
  leave_request: [
    { value: 'days', label: 'Days requested' },
    { value: 'leave_type_code', label: 'Leave type code' },
    { value: 'requires_hr_approval', label: 'Policy requires HR approval' },
  ],
  comp_off: [{ value: 'days', label: 'Days' }],
  regularisation: [{ value: 'date', label: 'Date' }],
  profile_change: [{ value: 'fields', label: 'Changed fields' }],
};
const OPS: { value: Op; label: string }[] = [
  { value: '==', label: 'equals' },
  { value: '!=', label: 'not equal' },
  { value: '>', label: '>' },
  { value: '>=', label: '≥' },
  { value: '<', label: '<' },
  { value: '<=', label: '≤' },
  { value: 'in', label: 'is one of' },
  { value: 'truthy', label: 'is true' },
];

const parseApprover = (a: string): { kind: ApproverKind; ref: string } => {
  if (a.startsWith('role:')) return { kind: 'role', ref: a.slice(5) };
  if (a.startsWith('user:')) return { kind: 'user', ref: a.slice(5) };
  return { kind: (a as ApproverKind) || 'reporting_manager', ref: '' };
};
const buildApprover = (kind: ApproverKind, ref: string) => (kind === 'role' ? `role:${ref}` : kind === 'user' ? `user:${ref}` : kind);
const whenValueText = (w: When | null | undefined) => (w?.value == null ? '' : Array.isArray(w.value) ? w.value.join(', ') : String(w.value));
const parseWhenValue = (op: Op, raw: string): unknown => {
  const conv = (s: string) => (s !== '' && !Number.isNaN(Number(s)) ? Number(s) : s === 'true' ? true : s === 'false' ? false : s);
  if (op === 'truthy') return undefined;
  if (op === 'in') return raw.split(',').map((s) => conv(s.trim())).filter((s) => s !== '');
  return conv(raw.trim());
};

function StepEditor({ step, index, count, wfKey, roles, users, onChange, onMove, onRemove }: {
  step: StepDef; index: number; count: number; wfKey: string;
  roles: { value: string; label: string }[]; users: { value: string; label: string }[];
  onChange: (s: StepDef) => void; onMove: (d: -1 | 1) => void; onRemove: () => void;
}) {
  const { colors } = useTheme();
  const ap = parseApprover(step.approver);
  const fields = CONTEXT_FIELDS[wfKey] ?? [];
  const setWhen = (w: When | null) => onChange({ ...step, when: w });
  return (
    <Card testID={`wf-step-${index}`}>
      <Row justify="space-between" wrap={false} style={{ marginBottom: 10 }}>
        <Row gap={8} wrap={false} style={{ flexShrink: 1 }}>
          <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }}>
            <Text size={12} weight="bold" color={colors.onPrimary}>{index + 1}</Text>
          </View>
          <Text weight="semibold" numberOfLines={1}>{step.name || `Step ${index + 1}`}</Text>
        </Row>
        <Row gap={2} wrap={false}>
          <IconButton icon="chevronUp" size="sm" label="Move step up" onPress={() => index > 0 && onMove(-1)} />
          <IconButton icon="chevronDown" size="sm" label="Move step down" onPress={() => index < count - 1 && onMove(1)} />
          <IconButton icon="trash" size="sm" label="Remove step" onPress={onRemove} />
        </Row>
      </Row>
      <Grid min={190} gap={12}>
        <TextField label="Step name" value={step.name} onChangeText={(name) => onChange({ ...step, name })} />
        <Select<ApproverKind>
          label="Approver"
          value={ap.kind}
          onChange={(k) => onChange({ ...step, approver: buildApprover(k, '') })}
          options={[
            { value: 'reporting_manager', label: 'Reporting manager' },
            { value: 'manager_of_manager', label: "Manager's manager" },
            { value: 'role', label: 'Anyone with role…' },
            { value: 'user', label: 'Specific user…' },
          ]}
        />
        {ap.kind === 'role' &&
          (roles.length ? (
            <Select label="Role" placeholder="Select role…" value={ap.ref || null} options={roles} onChange={(r) => onChange({ ...step, approver: buildApprover('role', r) })} />
          ) : (
            <TextField label="Role slug" placeholder="e.g. hr_admin" autoCapitalize="none" value={ap.ref} onChangeText={(r) => onChange({ ...step, approver: buildApprover('role', r) })} />
          ))}
        {ap.kind === 'user' &&
          (users.length ? (
            <Select label="User" placeholder="Select user…" value={ap.ref || null} options={users} onChange={(u) => onChange({ ...step, approver: buildApprover('user', u) })} />
          ) : (
            <TextField label="User ID" keyboardType="number-pad" value={ap.ref} onChangeText={(u) => onChange({ ...step, approver: buildApprover('user', u.replace(/\D/g, '')) })} />
          ))}
        <TextField
          label="Escalate after (days)"
          hint="Blank = never"
          keyboardType="number-pad"
          value={step.escalate_after_days == null ? '' : String(step.escalate_after_days)}
          onChangeText={(v) => onChange({ ...step, escalate_after_days: v.trim() === '' ? undefined : Number(v.replace(/\D/g, '')) })}
        />
      </Grid>
      <View style={{ marginTop: 12, gap: 8 }}>
        <Switch
          label="Only when a condition matches"
          description="Skip this step unless the request matches"
          value={!!step.when}
          onChange={(on) => setWhen(on ? { field: fields[0]?.value ?? '', op: '>', value: '' } : null)}
        />
        {!!step.when && (
          <Grid min={160} gap={12}>
            {fields.length ? (
              <Select label="Field" value={step.when.field || null} options={fields} onChange={(field) => setWhen({ ...step.when!, field })} />
            ) : (
              <TextField label="Field" autoCapitalize="none" value={step.when.field} onChangeText={(field) => setWhen({ ...step.when!, field })} />
            )}
            <Select<Op> label="Operator" value={step.when.op} options={OPS} onChange={(op) => setWhen({ ...step.when!, op })} />
            {step.when.op !== 'truthy' && (
              <TextField
                label="Value"
                hint={step.when.op === 'in' ? 'Comma separated, e.g. EL, SL' : undefined}
                autoCapitalize="none"
                value={whenValueText(step.when)}
                onChangeText={(t) => setWhen({ ...step.when!, value: t })}
              />
            )}
          </Grid>
        )}
      </View>
    </Card>
  );
}

/** Approval chains for leave, regularisation, profile changes and comp-off — defined as data. */
export function WorkflowsScreen() {
  const { can } = useAuth();
  const { isTablet } = useBreakpoint();
  const q = useQuery({ queryKey: ['workflow-definitions'], queryFn: () => api.workflow.definitions.list({ per_page: 200 }) });
  const roles = useQuery({ queryKey: ['roles', 'all'], queryFn: () => api.admin.roles.list({ per_page: 200 }), enabled: can(P.ROLES_MANAGE), retry: false });
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => api.admin.users.list({ per_page: 500 }), enabled: can(P.USERS_MANAGE), retry: false });
  const list = useMemo(() => rows(q.data), [q.data]);
  const [selId, setSelId] = useState<ID | 'new' | null>(null);
  const [d, setD] = useState<Def | null>(null);
  const [cond, setCond] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (selId === null && list.length) setSelId(list[0].id);
  }, [list, selId]);
  useEffect(() => {
    if (selId === 'new') setD({ key: 'leave_request', name: '', is_active: true, steps: [{ name: 'Manager approval', approver: 'reporting_manager', escalate_after_days: 2 }] });
    else {
      const x = list.find((w) => w.id === selId);
      if (x) setD(JSON.parse(JSON.stringify(x)) as Def);
    }
    setErrs({});
    setVersion((v) => v + 1);
  }, [selId, list]);
  useEffect(() => setCond(d?.conditions ? JSON.stringify(d.conditions, null, 2) : ''), [d?.id, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMut(
    () => {
      let conditions: Record<string, unknown> | null = null;
      if (cond.trim()) conditions = JSON.parse(cond);
      // condition values are edited as text; convert to numbers / booleans / lists for the engine
      const cleanSteps = (d?.steps ?? []).map((st) =>
        st.when ? { ...st, when: { ...st.when, value: parseWhenValue(st.when.op, whenValueText(st.when)) } } : { ...st, when: undefined },
      );
      const body = { ...d, steps: cleanSteps, conditions } as Partial<WorkflowDefinition>;
      return d?.id ? api.workflow.definitions.update(d.id, body) : api.workflow.definitions.create(body);
    },
    {
      invalidate: [['workflow-definitions']],
      success: 'Workflow saved — applies to new requests',
      onSuccess: (r) => setSelId(r.id),
      onError: (e) => setErrs(e instanceof SyntaxError ? { conditions: 'Invalid JSON' } : fieldErrors(e)),
    },
  );
  const steps = d?.steps ?? [];
  const setSteps = (s: StepDef[]) => setD({ ...d, steps: s });
  const roleList = rows(roles.data).map((r) => ({ value: r.slug, label: r.name }));
  const userList = rows(users.data).map((u) => ({ value: String(u.id), label: `${u.name} (${u.email})` }));
  const apLabel = (a: string) => {
    const p = parseApprover(a);
    if (p.kind === 'role') return `Role: ${roleList.find((r) => r.value === p.ref)?.label ?? (p.ref || '?')}`;
    if (p.kind === 'user') return `User: ${userList.find((u) => u.value === p.ref)?.label ?? `#${p.ref}`}`;
    return humanize(p.kind);
  };

  const menu = (
    <SideMenu
      title="Workflows"
      loading={q.isLoading}
      emptyTitle="No workflows"
      value={selId}
      onChange={setSelId}
      items={list.map((w) => ({ key: w.id as ID | 'new', title: w.name, subtitle: `${humanize(w.key)} · v${w.version}`, right: !w.is_active ? <Badge>Off</Badge> : undefined }))}
      testID="wf-list"
    />
  );
  const editor = d ? (
    <Stack key={`${String(selId)}-${version}`}>
      <Card title={d.id ? `${d.name} · v${d.version}` : 'New workflow'} actions={<Button variant="primary" title="Save" loading={save.isPending} onPress={() => save.mutate(undefined)} testID="wf-save" />}>
        <Grid min={200} gap={12}>
          <TextField label="Name" required error={errs.name} value={d.name ?? ''} onChangeText={(name) => setD({ ...d, name })} testID="wf-name" />
          <Select label="Applies to" error={errs.key} value={d.key ?? null} options={KEYS.map((k) => ({ value: k, label: humanize(k) }))} onChange={(key) => setD({ ...d, key })} />
          <View style={{ paddingTop: 22 }}>
            <Switch label="Active" value={!!d.is_active} onChange={(is_active) => setD({ ...d, is_active })} />
          </View>
        </Grid>
        <Row gap={6} style={{ marginTop: 16 }}>
          <Badge tone="info">Request submitted</Badge>
          {steps.map((s, i) => (
            <Row key={i} gap={6}>
              <Icon name="chevronRight" size={14} />
              <Badge tone="primary">{`${s.name || `Step ${i + 1}`} · ${apLabel(s.approver)}${s.escalate_after_days ? ` · ⏱${s.escalate_after_days}d` : ''}${s.when ? ' · if…' : ''}`}</Badge>
            </Row>
          ))}
          <Icon name="chevronRight" size={14} />
          <Badge tone="success">Approved</Badge>
        </Row>
      </Card>
      {!!errs.steps && <Alert tone="danger">{errs.steps}</Alert>}
      {steps.map((s, i) => (
        <StepEditor
          key={`${i}-${steps.length}`}
          step={s}
          index={i}
          count={steps.length}
          wfKey={d.key ?? ''}
          roles={roleList}
          users={userList}
          onChange={(ns) => setSteps(steps.map((x, j) => (j === i ? ns : x)))}
          onMove={(dir) => {
            const n = [...steps];
            [n[i], n[i + dir]] = [n[i + dir], n[i]];
            setSteps(n);
          }}
          onRemove={() => setSteps(steps.filter((_, j) => j !== i))}
        />
      ))}
      <Button icon="plus" title="Add step" onPress={() => setSteps([...steps, { name: `Step ${steps.length + 1}`, approver: 'reporting_manager' }])} testID="wf-add-step" />
      <Card title="Definition conditions (optional)" subtitle='JSON, e.g. {"min_days": 3} to apply only for leave longer than 3 days'>
        <TextField accessibilityLabel="Conditions JSON" multiline style={{ fontFamily: 'monospace', fontSize: 12 }} value={cond} onChangeText={setCond} error={errs.conditions} />
      </Card>
    </Stack>
  ) : q.isLoading ? (
    <Loading />
  ) : (
    <Card>
      <EmptyState icon="workflow" title="Select a workflow" />
    </Card>
  );

  return (
    <Screen testID="workflows">
      <PageHeader title="Workflow builder" subtitle="Approval chains defined as data — edits apply to new requests only." actions={<Button variant="primary" icon="plus" title="New workflow" onPress={() => setSelId('new')} testID="wf-new" />} />
      {isTablet ? (
        <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
          <View style={{ width: 260 }}>{menu}</View>
          <View style={{ flex: 1, minWidth: 0 }}>{editor}</View>
        </View>
      ) : (
        <Stack>
          {menu}
          {editor}
        </Stack>
      )}
    </Screen>
  );
}
